import { fetchChartingCollectionByEntry } from '@/features/charting-next/api/chartingCollectionRepository';
import { resolveChartingCapabilities } from '@/features/charting-next/models/chartingCapabilities';
import { useChartingContextStore } from '@/features/charting-next/stores/chartingContext';
import { useChartingSession } from '@/features/charting-next/services/chartingSession';
import { computed } from '@vue/composition-api';
import { marketSortToChartingSort } from '@/features/charting-next/models/chartingMarketQuery';
import { watch } from '@vue/composition-api';
import { useChartingCollections } from './useChartingCollections';
import { useChartingFavorites } from './useChartingFavorites';
import { useChartingFinderDefinition } from './useChartingFinderDefinition';
import { useChartingMetricPreferences } from './useChartingMetricPreferences';
import { useChartingMarketExplorer } from './useChartingMarketExplorer';
import { useChartingRouteState } from './useChartingRouteState';
import { useChartingSavedSearches } from './useChartingSavedSearches';

export function useChartingWorkspace(route, router, { enableSelectedOhlc = true } = {}) {
    const sessionStore = useChartingSession();
    const contextStore = useChartingContextStore();
    const capabilities = computed(() => resolveChartingCapabilities({ features: sessionStore.features, user: sessionStore.user }));

    const routeState = useChartingRouteState({
        route,
        router,
        contextStore
    });
    const marketExplorer = useChartingMarketExplorer({
        sessionStore,
        route,
        entry: routeState.entry,
        openSource: routeState.openSource
    });
    const finderState = useChartingFinderDefinition({
        sessionStore,
        capabilities,
        entry: routeState.entry
    });
    const collections = useChartingCollections({
        sessionStore,
        route,
        entry: routeState.entry,
        contextStore,
        syncSelectedTickerInRoute: routeState.syncSelectedTickerInRoute,
        finderRevision: finderState.finderRevision,
        marketExplorer,
        enableOhlcSeries: enableSelectedOhlc,
        loadCollectionEntry: fetchChartingCollectionByEntry
    });
    const favorites = useChartingFavorites({
        sessionStore,
        capabilities,
        selectedStock: collections.selectedStock,
        refreshCollection: () => collections.collectionResource.refresh(),
        patchCollectionFavorite: marketExplorer.patchFavoriteMembership
    });
    const savedSearches = useChartingSavedSearches({
        sessionStore,
        capabilities,
        marketExplorer
    });
    const metricPreferences = useChartingMetricPreferences({
        sessionStore,
        selectedStock: collections.selectedStock
    });

    watch(
        () => marketExplorer.state.value?.appliedQuery?.sort,
        (sort) => {
            if (marketExplorer.isActive.value && sort) contextStore.setResultSort(marketSortToChartingSort(sort));
        },
        { deep: true }
    );

    const refreshWorkspace = async () => {
        await Promise.all([collections.collectionResource.refresh(), favorites.favoriteFoldersResource.refresh(), savedSearches.savedSearchesResource.refresh()]);
    };

    return {
        entry: routeState.entry,
        capabilities,
        marketExplorer,
        legacySourceHref: routeState.legacySourceHref,
        tickerSearch: routeState.tickerSearch,
        descriptionSearch: routeState.descriptionSearch,
        openSource: routeState.openSource,
        setResolution: routeState.setResolution,
        setPriceSeriesType: value => contextStore.setPriceSeriesType(value),
        setWorkspaceViewMode: value => contextStore.setWorkspaceViewMode(value),
        submitTickerSearch: routeState.submitTickerSearch,
        submitDescriptionSearch: routeState.submitDescriptionSearch,
        toggleShowVolume: () => contextStore.toggleShowVolume(),
        toggleOverlayVisibility: key => contextStore.toggleOverlayVisibility(key),
        contextStore,
        finderDefinitionResource: finderState.finderDefinitionResource,
        activeFinderBase: finderState.activeFinderBase,
        mutableFinderBase: finderState.mutableFinderBase,
        refreshActiveFinderDefinition: finderState.refreshActiveFinderDefinition,
        refreshMutableFinderDefinition: finderState.refreshMutableFinderDefinition,
        collectionResource: collections.collectionResource,
        results: collections.results,
        totalResults: collections.totalResults,
        hasMoreResults: collections.hasMoreResults,
        isLoadingNextPage: collections.isLoadingNextPage,
        loadNextPage: collections.loadNextPage,
        resultSortOptions: collections.resultSortOptions,
        resultSortValue: collections.resultSortValue,
        setResultSort: value => {
            contextStore.setResultSort(value);
            if (marketExplorer.isActive.value) marketExplorer.applySort(value).catch(() => {});
        },
        workspaceViewMode: computed(() => contextStore.workspaceViewMode),
        selectedStock: collections.selectedStock,
        ohlcSeriesResource: collections.ohlcSeriesResource,
        hasPreviousTicker: collections.hasPreviousTicker,
        hasNextTicker: collections.hasNextTicker,
        selectTicker: collections.selectTicker,
        selectEntryVariant: collections.selectEntryVariant,
        selectPreviousTicker: collections.selectPreviousTicker,
        selectNextTicker: collections.selectNextTicker,
        favoriteFoldersResource: favorites.favoriteFoldersResource,
        isMutatingFavorites: favorites.isMutatingFavorites,
        favoriteMutationError: favorites.favoriteMutationError,
        toggleFavoriteFolder: favorites.toggleFavoriteFolder,
        toggleFavoriteFolderForStock: favorites.toggleFavoriteFolderForStock,
        createFavoriteFolder: favorites.createFavoriteFolder,
        renameFavoriteFolder: favorites.renameFavoriteFolder,
        removeFavoriteFolder: favorites.removeFavoriteFolder,
        savedSearchesResource: savedSearches.savedSearchesResource,
        metricPreferencesResource: metricPreferences.metricPreferencesResource,
        chartMetrics: metricPreferences.metrics,
        chartTableColumns: metricPreferences.tableColumns,
        availableChartMetrics: metricPreferences.availableMetrics,
        isMutatingChartMetrics: metricPreferences.isMutatingMetrics,
        chartMetricMutationError: metricPreferences.metricMutationError,
        addChartMetric: metricPreferences.addMetric,
        removeChartMetric: metricPreferences.removeMetric,
        createSavedSearch: savedSearches.createSavedSearch,
        renameSavedSearch: savedSearches.renameSavedSearch,
        removeSavedSearch: savedSearches.removeSavedSearch,
        refreshSavedSearches: savedSearches.refreshSavedSearches,
        refreshWorkspace
    };
}
